import { useParams, Link } from 'react-router-dom'
import { ProjectElementType } from '@kfolks/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useProject } from '@/hooks/useClients'
import { useProjectElements } from '@/hooks/useProjectElements'
import { ArrowLeft, Loader2, ExternalLink } from 'lucide-react'

const ELEMENT_TYPE_LABELS: Record<string, string> = {
	[ProjectElementType.GITHUB_REPO]: 'GitHub Repo',
	[ProjectElementType.DATABASE]: 'Database',
	[ProjectElementType.CLOUD_INFRASTRUCTURE]: 'Cloud Infrastructure',
	[ProjectElementType.CI_CD_PIPELINE]: 'CI/CD Pipeline',
	[ProjectElementType.DOMAIN]: 'Domain',
	[ProjectElementType.MONITORING]: 'Monitoring',
	[ProjectElementType.DOCUMENTATION]: 'Documentation',
	[ProjectElementType.COMMUNICATION_CHANNEL]: 'Communication',
	[ProjectElementType.STORAGE]: 'Storage',
	[ProjectElementType.API]: 'API',
	[ProjectElementType.OTHER]: 'Other',
}

export function ProjectDetailPage() {
	const { id } = useParams<{ id: string }>()
	const { data: project, isLoading, error } = useProject(id ?? '')
	const { data: elements = [] } = useProjectElements(id ?? null)
	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-24">
				<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
			</div>
		)
	}

	if (error || !project) {
		return (
			<div className="space-y-4">
				<Link to="/projects">
					<Button variant="ghost" size="sm">
						<ArrowLeft className="h-4 w-4 mr-1" /> Back to Projects
					</Button>
				</Link>
				<div className="rounded-lg bg-red-50 p-6 text-center text-red-700">Project not found</div>
			</div>
		)
	}

	// Group elements by type
	const grouped = elements.reduce(
		(acc, el) => {
			const type = el.elementType
			if (!acc[type]) acc[type] = []
			acc[type].push(el)
			return acc
		},
		{} as Record<string, typeof elements>,
	)

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Link to="/projects">
					<Button variant="ghost" size="sm">
						<ArrowLeft className="h-4 w-4 mr-1" /> Projects
					</Button>
				</Link>
				<div>
					<h1 className="text-2xl font-bold text-gray-900">{project.displayName || project.code}</h1>
					<p className="text-sm font-mono text-gray-500">{project.code}</p>
				</div>
				<Badge variant={project.isActive ? 'default' : 'destructive'} className="ml-auto">
					{project.isActive ? 'Active' : 'Inactive'}
				</Badge>
			</div>

			{project.description && <p className="text-sm text-gray-600">{project.description}</p>}

			<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-gray-500 mb-1">Owner</h3>
						<span className="text-sm text-gray-900">{project.ownerEmail || 'Not assigned'}</span>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-gray-500 mb-1">Project Manager</h3>
						<span className="text-sm text-gray-900">{project.pmEmail || 'Not assigned'}</span>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-gray-500 mb-1">Elements</h3>
						<span className="text-sm font-medium text-gray-900">{elements.length}</span>
					</CardContent>
				</Card>
			</div>

			<div>
				<h2 className="text-lg font-semibold text-gray-900 mb-4">Project Elements</h2>
				{elements.length === 0 ? (
					<p className="text-sm text-gray-400 py-8 text-center">No elements added yet</p>
				) : (
					<div className="space-y-4">
						{Object.entries(grouped).map(([type, items]) => (
							<div key={type}>
								<h3 className="text-sm font-medium text-gray-700 mb-2">
									{ELEMENT_TYPE_LABELS[type] ?? type} ({items.length})
								</h3>
								<div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
									{items.map((el) => (
										<Card key={el.elementId}>
											<CardContent className="p-3">
												<div className="flex items-start justify-between">
													<div>
														<p className="text-sm font-medium text-gray-900">{el.name}</p>
														{el.description && <p className="text-xs text-gray-500 mt-0.5">{el.description}</p>}
														{el.environment && (
															<Badge variant="outline" className="mt-1 text-xs">
																{el.environment}
															</Badge>
														)}
													</div>
													{el.url && (
														<a
															href={el.url}
															target="_blank"
															rel="noopener noreferrer"
															className="text-gray-400 hover:text-gray-600"
														>
															<ExternalLink className="h-4 w-4" />
														</a>
													)}
												</div>
											</CardContent>
										</Card>
									))}
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
